//PayrollReport.js
import React, { useEffect, useMemo, useState } from "react";
import "../styles/PayrollReport.css";
import { DateTime } from "luxon";

import { db } from "../firebase/config";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import { useAuth } from "../Context/AuthContext";
import Lottie from "lottie-react";
import loadingAnimation from "../tools/Animations/Loading Dots Blue.json";

import {
  getPayrollConfigured,
  setPayrollConfigured,
} from "../utils/firstRunFlags"; // ← adjust path if needed
import { SlidersHorizontal } from "lucide-react";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/** Money formatter (USD for now – you can make this dynamic later) */
const money = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });

// ─────────────────────────────────────────────────────────────
// Overnight shift helpers (daily overtime accuracy)
// ─────────────────────────────────────────────────────────────

/** Convert Firestore Timestamp | Date | millis | ISO string -> Luxon DateTime in company TZ */
const toDT = (v, tz) => {
  if (!v) return null;

  // Firestore Timestamp
  if (typeof v === "object" && typeof v.toDate === "function") {
    return DateTime.fromJSDate(v.toDate(), { zone: tz });
  }

  // JS Date
  if (v instanceof Date) {
    return DateTime.fromJSDate(v, { zone: tz });
  }

  // millis number
  if (typeof v === "number") {
    return DateTime.fromMillis(v, { zone: tz });
  }

  // ISO string
  if (typeof v === "string") {
    const dt = DateTime.fromISO(v, { zone: tz });
    return dt.isValid ? dt : null;
  }

  return null;
};

/**
 * Split a (clockIn -> clockOut) interval into { "YYYY-MM-DD": hours } buckets
 * in company TZ. (No pay-period clamping.)
 */
const splitIntoDailyBuckets = ({ clockIn, clockOut, tz }) => {
  const inDT = toDT(clockIn, tz);
  const outDT = toDT(clockOut, tz);

  if (!inDT || !outDT || !inDT.isValid || !outDT.isValid) return null;

  const start = inDT;
  const end = outDT;
  if (end <= start) return null;

  const buckets = {};
  let cursor = start;

  while (cursor < end) {
    const dayStart = cursor.startOf("day");
    const nextDayStart = dayStart.plus({ days: 1 });
    const segEnd = end < nextDayStart ? end : nextDayStart;

    const hours = segEnd.diff(cursor, "seconds").seconds / 3600;
    const key = cursor.toISODate(); // YYYY-MM-DD in company TZ
    buckets[key] = (buckets[key] || 0) + hours;

    cursor = segEnd;
  }

  return buckets;
};

function PageLoader({ label = "Loading…" }) {
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
      }}
    >
      <Lottie
        animationData={loadingAnimation}
        loop
        style={{ width: 120, height: 120 }}
      />
      {label && <div style={{ color: "#64748b", fontSize: 14 }}>{label}</div>}
    </div>
  );
}

export default function PayrollReport() {
  const { user } = useAuth();

  const [companyId, setCompanyId] = useState(null);
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");
  const [loadingInit, setLoadingInit] = useState(true);
  const [error, setError] = useState("");

  const [employees, setEmployees] = useState({}); // map: id -> data

  // ── Report settings ──────────────────────────────────────────────
  // "weekly" | "biweekly" | "semimonthly" (comes from Payroll settings)
  const [periodType, setPeriodType] = useState("weekly");
  const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
  // ✅ NEW: auto vs manual pay period behavior
  const [periodMode, setPeriodMode] = useState("auto"); // "auto" | "manual"
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("all");

  // ✅ NEW: Worksite filter
  const [selectedWorkSiteId, setSelectedWorkSiteId] = useState("all");

  const [isGenerating, setIsGenerating] = useState(false);

  // Report data
  const [rows, setRows] = useState([]);
  const [rangeLabel, setRangeLabel] = useState("");

  // ── Payroll config (from Settings modal) ─────────────────────────
  const [payrollConfig, setPayrollConfig] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  // First-run banner like Expense
  const [payrollBannerOpen, setPayrollBannerOpen] = useState(false);

  // Local editable fields inside modal
  const [cfgFrequency, setCfgFrequency] = useState("weekly");
  const [cfgAnchorDate, setCfgAnchorDate] = useState("");

  // ⚙️ Payroll now only owns "whether to pay OT" + multiplier
  const [cfgOvertimeEnabled, setCfgOvertimeEnabled] = useState(false);
  const [cfgOvertimeMultiplier, setCfgOvertimeMultiplier] = useState(1.5);

  // 🔁 Time Tracking overtime config (source of truth for rules)
  const [ttOvertimeConfig, setTtOvertimeConfig] = useState(null);

  // ── Bootstrap: load company + employees + saved filters ──────────
  useEffect(() => {
    if (!user?.uid) return;

    (async () => {
      try {
        setLoadingInit(true);

        // 1) Get user -> companyId + any saved payroll filters
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data() || {};
        const cid = userData.companyId;
        if (!cid) {
          setError("Company not found for this user.");
          setLoadingInit(false);
          return;
        }
        setCompanyId(cid);

        // Saved filters (scoped to this company)
        const savedFilters = userData.payrollReportFilters;
        let savedStartDate = null;
        let savedEmployeeId = "all";
        let savedWorkSiteId = "all";
        let savedPeriodMode = "auto"; // ✅ NEW

        if (savedFilters && savedFilters.companyId === cid) {
          if (savedFilters.startDate) savedStartDate = savedFilters.startDate;
          if (savedFilters.periodMode)
            savedPeriodMode = savedFilters.periodMode;

          if (savedFilters.selectedEmployeeId)
            savedEmployeeId = savedFilters.selectedEmployeeId;

          // ✅ NEW
          if (savedFilters.selectedWorkSiteId)
            savedWorkSiteId = savedFilters.selectedWorkSiteId;
        }

        // Apply saved filters immediately
        setSelectedEmployeeId(savedEmployeeId);
        setSelectedWorkSiteId(savedWorkSiteId);
        setPeriodMode(savedPeriodMode); // ✅ NEW
        // 2) Company details (time zone + payrollConfig)
        const companyRef = doc(db, "companies", cid);
        const companySnap = await getDoc(companyRef);
        const companyData = companySnap.data() || {};
        const tz = companyData.timeZone || "UTC";
        setCompanyTimeZone(tz);

        const existingConfig = companyData.payrollConfig || null;
        if (existingConfig) {
          setPayrollConfig(existingConfig);
          setCfgFrequency(existingConfig.frequency || "weekly");
          setCfgAnchorDate(existingConfig.anchorDate || "");

          setCfgOvertimeEnabled(!!existingConfig.overtimeEnabled);
          setCfgOvertimeMultiplier(existingConfig.overtimeMultiplier || 1.5);

          // Drive the main controls from saved config if present
          if (
            ["weekly", "biweekly", "semimonthly"].includes(
              existingConfig.frequency,
            )
          ) {
            setPeriodType(existingConfig.frequency);
          }
        }

        // 3) Time Tracking overtime rules (source of truth)
        try {
          const ttRef = doc(db, `companies/${cid}/settings/timeTracking`);
          const ttSnap = await getDoc(ttRef);
          if (ttSnap.exists()) {
            const d = ttSnap.data() || {};
            const basisRaw = d.overtimeBasis || "period"; // "period" | "daily" | legacy "weekly"
            const normalizedBasis =
              basisRaw === "weekly" ? "period" : basisRaw || "period";

            setTtOvertimeConfig({
              overtimeBasis: normalizedBasis,
              hoursBeforeOvertimePerPeriod:
                typeof d.hoursBeforeOvertimePerPeriod === "number"
                  ? d.hoursBeforeOvertimePerPeriod
                  : 40,
              dailyOvertimeHours:
                typeof d.dailyOvertimeHours === "number"
                  ? d.dailyOvertimeHours
                  : 8,
            });
          } else {
            // Default if they haven't configured yet
            setTtOvertimeConfig({
              overtimeBasis: "period",
              hoursBeforeOvertimePerPeriod: 40,
              dailyOvertimeHours: 8,
            });
          }
        } catch (e) {
          console.warn("Failed to load Time Tracking overtime config:", e);
          setTtOvertimeConfig({
            overtimeBasis: "period",
            hoursBeforeOvertimePerPeriod: 40,
            dailyOvertimeHours: 8,
          });
        }

        // 4) Employees map (for name, job title, hourly rate, department, + worksite fields)
        const employeesRef = collection(db, `companies/${cid}/employees`);
        const employeesSnap = await getDocs(employeesRef);
        const map = {};
        employeesSnap.forEach((d) => {
          map[d.id] = { id: d.id, ...d.data() };
        });
        setEmployees(map);

        // 5) Decide initial start date:
        //    savedFilters.startDate > companyConfig.anchorDate > default Monday
        // 5) Decide initial start date:
        //    If periodMode is "manual": use savedStartDate > anchorDate > Monday
        //    If periodMode is "auto": compute most recently ended period from anchor+frequency (fallback to Monday)
        let startDateToUse = null;

        const freqToUse =
          existingConfig?.frequency &&
          ["weekly", "biweekly", "semimonthly"].includes(
            existingConfig.frequency,
          )
            ? existingConfig.frequency
            : periodType; // fallback (though periodType may update below)

        const anchorToUse = existingConfig?.anchorDate || null;

        // If saved mode is manual, honor savedStartDate first
        if (savedPeriodMode === "manual") {
          startDateToUse = savedStartDate || anchorToUse;
          if (!startDateToUse) {
            const today = DateTime.now()
              .setZone(tz)
              .startOf("day");
            startDateToUse = today.startOf("week").toISODate();
          }
        } else {
          // auto mode: most recently ended
          const auto = anchorToUse
            ? computeMostRecentEndedPeriodStart({
                anchorISO: anchorToUse,
                periodType: existingConfig?.frequency || "weekly",
                tz,
              })
            : null;

          if (auto) startDateToUse = auto;
          else {
            const today = DateTime.now()
              .setZone(tz)
              .startOf("day");
            startDateToUse = today.startOf("week").toISODate();
          }
        }

        setStartDate(startDateToUse);

        setLoadingInit(false);
      } catch (e) {
        console.error("Failed to load payroll bootstrap data", e);
        setError("Unable to load payroll data.");
        setLoadingInit(false);
      }
    })();
  }, [user?.uid, periodType]);

  // ── First-run: check payrollConfigured and auto-open settings ─────
  useEffect(() => {
    if (!companyId) return;

    (async () => {
      try {
        const configured = await getPayrollConfigured(db, companyId);
        if (!configured) {
          setPayrollBannerOpen(true);
          setShowConfigModal(true); // auto-open settings like Expense
        }
      } catch (e) {
        console.error("Failed to check payroll first-run", e);
      }
    })();
  }, [companyId]);

  // ── Persist payroll report filters (per user + company) ──────────
  useEffect(() => {
    if (!user?.uid || !companyId || !startDate) return;

    const saveFilters = async () => {
      try {
        const userRef = doc(db, "users", user.uid);
        await setDoc(
          userRef,
          {
            payrollReportFilters: {
              companyId,
              periodMode, // ✅ NEW
              selectedEmployeeId,
              selectedWorkSiteId, // ✅ NEW
              startDate,
            },
          },
          { merge: true },
        );
      } catch (e) {
        console.error("Failed to save payrollReportFilters", e);
      }
    };

    saveFilters();
  }, [
    user?.uid,
    companyId,
    periodMode,
    selectedEmployeeId,
    selectedWorkSiteId,
    startDate,
  ]);

  const employeeOptions = useMemo(() => {
    return Object.values(employees).sort((a, b) => {
      const na = a.name || a.fullName || a.displayName || "Unknown employee A";
      const nb = b.name || b.fullName || b.displayName || "Unknown employee B";
      return na.localeCompare(nb);
    });
  }, [employees]);

  // ✅ NEW: Worksite options derived from employees collection
  const workSiteOptions = useMemo(() => {
    const map = new Map(); // locationId -> locationName
    Object.values(employees).forEach((e) => {
      const id = e.locationId || null;
      const name = e.locationName || "";
      if (id) map.set(id, name || "Unnamed site");
    });

    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [employees]);

  // Friendly labels for schedule + anchor (read-only)
  const scheduleLabel = useMemo(() => {
    const freq = payrollConfig?.frequency || cfgFrequency || "weekly";
    if (freq === "weekly") return "Weekly pay period";
    if (freq === "biweekly") return "Bi-weekly pay period";
    if (freq === "semimonthly") return "Semi-monthly pay period";
    return "Pay period";
  }, [payrollConfig, cfgFrequency]);

  const anchorDisplay = useMemo(() => {
    const anchor = payrollConfig?.anchorDate || cfgAnchorDate;
    if (!anchor) return "Not set";
    const tz = companyTimeZone || "UTC";
    try {
      return DateTime.fromISO(anchor, { zone: tz }).toFormat("MMM dd, yyyy");
    } catch {
      return anchor;
    }
  }, [payrollConfig, cfgAnchorDate, companyTimeZone]);

  // ─────────────────────────────────────────────────────────────
  // Pay period helpers (anchor-based, realistic payroll navigation)
  // ─────────────────────────────────────────────────────────────

  function computeMostRecentEndedPeriodStart({ anchorISO, periodType, tz }) {
    if (!anchorISO) return null;

    const anchor = DateTime.fromISO(anchorISO, { zone: tz }).startOf("day");
    const today = DateTime.now()
      .setZone(tz)
      .startOf("day");

    if (!anchor.isValid) return null;

    // For weekly/biweekly: periods are fixed-length.
    if (periodType === "weekly" || periodType === "biweekly") {
      const lenDays = periodType === "weekly" ? 7 : 14;
      const diffDays = Math.floor(today.diff(anchor, "days").days);

      // If today is before anchor, just use anchor.
      if (diffDays < 0) return anchor.toISODate();

      // "current" period index
      const idx = Math.floor(diffDays / lenDays);
      const currentStart = anchor.plus({ days: idx * lenDays }).startOf("day");
      const currentEnd = currentStart.plus({ days: lenDays - 1 }).endOf("day");

      // We want the MOST RECENT ENDED period:
      // - if current period already ended (today > end), use currentStart
      // - else use previous period start (idx-1)
      if (today > currentEnd) return currentStart.toISODate();

      const prevStart = anchor.plus({ days: Math.max(0, (idx - 1) * lenDays) });
      return prevStart.startOf("day").toISODate();
    }

    // Semi-monthly: periods are 1–15 and 16–end
    // "Most recently ended" means:
    // - if today is 1–15 => most recently ended is 16–end of previous month (start: prev month day 16)
    // - if today is 16–end => most recently ended is 1–15 of this month (start: this month day 1)
    if (periodType === "semimonthly") {
      if (today.day <= 15) {
        const prevMonth = today.minus({ months: 1 });
        return prevMonth
          .set({ day: 16 })
          .startOf("day")
          .toISODate();
      }
      return today
        .startOf("month")
        .startOf("day")
        .toISODate(); // 1st
    }

    return anchor.toISODate();
  }

  function shiftPeriodStart({ startISO, periodType, tz, direction }) {
    // direction: -1 (prev) | +1 (next)
    const start = DateTime.fromISO(startISO, { zone: tz }).startOf("day");
    if (!start.isValid) return startISO;

    if (periodType === "weekly") {
      return start.plus({ days: 7 * direction }).toISODate();
    }
    if (periodType === "biweekly") {
      return start.plus({ days: 14 * direction }).toISODate();
    }
    if (periodType === "semimonthly") {
      // Move between 1st and 16th boundaries
      if (direction < 0) {
        // prev
        if (start.day <= 15) {
          // go to previous month 16th
          const prevMonth = start.minus({ months: 1 });
          return prevMonth
            .set({ day: 16 })
            .startOf("day")
            .toISODate();
        }
        // from 16th -> go to 1st of same month
        return start
          .startOf("month")
          .startOf("day")
          .toISODate();
      } else {
        // next
        if (start.day <= 15) {
          // 1st -> 16th same month
          return start
            .set({ day: 16 })
            .startOf("day")
            .toISODate();
        }
        // 16th -> 1st of next month
        const nextMonth = start.plus({ months: 1 });
        return nextMonth
          .startOf("month")
          .startOf("day")
          .toISODate();
      }
    }

    return startISO;
  }

  // Compute effective date range for the selected cycle
  const effectiveRange = useMemo(() => {
    if (!startDate) return null;

    const tz = companyTimeZone || "UTC";
    const start = DateTime.fromISO(startDate, { zone: tz }).startOf("day");
    let end;

    if (periodType === "weekly") {
      end = start.plus({ days: 6 }).endOf("day");
    } else if (periodType === "biweekly") {
      end = start.plus({ days: 13 }).endOf("day");
    } else if (periodType === "semimonthly") {
      // 1st–15th, or 16th–end of month depending on start date
      if (start.day <= 15) {
        end = start.set({ day: 15 }).endOf("day");
      } else {
        end = start.endOf("month");
      }
    } else {
      // Fallback: same-day period
      end = start.endOf("day");
    }

    return { start, end };
  }, [startDate, periodType, companyTimeZone]);

  const goToMostRecentEnded = () => {
    const tz = companyTimeZone || "UTC";
    const anchorISO = payrollConfig?.anchorDate || cfgAnchorDate || null;
    const freq = payrollConfig?.frequency || periodType || "weekly";

    if (!anchorISO) {
      toast.info(
        "Set an anchor date in Payroll settings to use Auto pay periods.",
      );
      return;
    }

    const autoStart = computeMostRecentEndedPeriodStart({
      anchorISO,
      periodType: freq,
      tz,
    });

    if (autoStart) {
      setPeriodMode("auto");
      setStartDate(autoStart);
    }
  };

  const goPrev = () => {
    const tz = companyTimeZone || "UTC";
    const freq = payrollConfig?.frequency || periodType || "weekly";
    setPeriodMode("manual");
    setStartDate((prev) =>
      shiftPeriodStart({ startISO: prev, periodType: freq, tz, direction: -1 }),
    );
  };

  const goNext = () => {
    const tz = companyTimeZone || "UTC";
    const freq = payrollConfig?.frequency || periodType || "weekly";
    setPeriodMode("manual");
    setStartDate((prev) =>
      shiftPeriodStart({ startISO: prev, periodType: freq, tz, direction: +1 }),
    );
  };

  // Pretty label in the corner
  useEffect(() => {
    if (!effectiveRange) {
      setRangeLabel("");
      return;
    }
    const { start, end } = effectiveRange;
    const sameMonth = start.month === end.month && start.year === end.year;
    const label = sameMonth
      ? `${start.toFormat("MMM dd")} – ${end.toFormat("dd, yyyy")}`
      : `${start.toFormat("MMM dd, yyyy")} – ${end.toFormat("MMM dd, yyyy")}`;
    setRangeLabel(label);
  }, [effectiveRange]);

  // ── Generate payroll report ──────────────────────────────────────
  const handleGenerate = async () => {
    if (!companyId || !effectiveRange) return;
    const { start, end } = effectiveRange;

    try {
      setIsGenerating(true);
      setError("");

      const startISO = start.toISODate();
      const endISO = end.toISODate();
      const tz = companyTimeZone || "UTC";

      // time_tracking.date is stored as YYYY-MM-DD (company TZ)
      const baseRef = collection(db, `companies/${companyId}/time_tracking`);
      const qRef = query(
        baseRef,
        where("date", ">=", startISO),
        where("date", "<=", endISO),
      );
      const snap = await getDocs(qRef);

      // 🔹 Aggregate by employee + per day, separating work vs leave
      // userId -> {
      //   totalHours, workHours, paidLeaveHours, unpaidLeaveHours,
      //   entries, perDayWork: { [date]: hours }, siteHours: { [siteId]: hours }, siteNames: { [siteId]: name }
      // }
      const agg = {};

      snap.forEach((docSnap) => {
        const d = docSnap.data();
        const id = d.userId;
        if (!id) return;

        // Employee filter
        if (selectedEmployeeId !== "all" && id !== selectedEmployeeId) return;

        // Worksite filter (based on entry's workSiteId)
        const entryWorkSiteId = d.workSiteId || null;
        const entryWorkSiteName = d.workSiteName || "";

        if (selectedWorkSiteId !== "all") {
          if (!entryWorkSiteId || entryWorkSiteId !== selectedWorkSiteId)
            return;
        }

        const hrs = Number(d.hoursWorked || 0);
        const date = d.date; // "YYYY-MM-DD" in company TZ
        const entryType = d.entryType || ""; // e.g. "work", "paid-leave"
        const status = d.status || "";

        // 🔹 Basic classification for leave vs work
        const isOnLeaveStatus = status === "On Leave";
        const isPaidLeave =
          entryType === "paid-leave" ||
          d.leavePaid === true ||
          d.isPaidLeave === true;
        const isAnyLeave =
          isPaidLeave || entryType === "unpaid-leave" || isOnLeaveStatus;
        const isUnpaidLeave = isAnyLeave && !isPaidLeave;

        if (!agg[id]) {
          agg[id] = {
            totalHours: 0,
            workHours: 0,
            paidLeaveHours: 0,
            unpaidLeaveHours: 0,
            entries: 0,
            perDayWork: {},
            siteHours: {}, // ✅ NEW
            siteNames: {}, // ✅ NEW
          };
        }

        agg[id].entries += 1;
        agg[id].totalHours += hrs;

        if (isPaidLeave) {
          agg[id].paidLeaveHours += hrs;
        } else if (isUnpaidLeave) {
          agg[id].unpaidLeaveHours += hrs;
        } else {
          // ✅ Worked time: split across calendar days (overnight-safe) for daily OT accuracy
          const buckets = splitIntoDailyBuckets({
            clockIn: d.clockIn,
            clockOut: d.clockOut,
            tz,
          });

          if (buckets) {
            Object.entries(buckets).forEach(([dayISO, h]) => {
              const hh = Number(h || 0);
              if (hh <= 0) return;

              agg[id].workHours += hh;
              agg[id].perDayWork[dayISO] =
                (agg[id].perDayWork[dayISO] || 0) + hh;

              // ✅ NEW: site aggregation (work time only)
              if (entryWorkSiteId) {
                agg[id].siteHours[entryWorkSiteId] =
                  (agg[id].siteHours[entryWorkSiteId] || 0) + hh;

                if (!agg[id].siteNames[entryWorkSiteId]) {
                  agg[id].siteNames[entryWorkSiteId] =
                    entryWorkSiteName || "Unnamed site";
                }
              }
            });
          } else {
            // Fallback: if no timestamps exist, use stored date + hoursWorked (legacy behavior)
            agg[id].workHours += hrs;
            if (date) {
              agg[id].perDayWork[date] = (agg[id].perDayWork[date] || 0) + hrs;
            }

            // ✅ NEW: site aggregation (work time only)
            if (entryWorkSiteId) {
              agg[id].siteHours[entryWorkSiteId] =
                (agg[id].siteHours[entryWorkSiteId] || 0) + hrs;

              if (!agg[id].siteNames[entryWorkSiteId]) {
                agg[id].siteNames[entryWorkSiteId] =
                  entryWorkSiteName || "Unnamed site";
              }
            }
          }
        }
      });

      const config = payrollConfig || {};
      const overtimePayOn = config.overtimeEnabled ?? cfgOvertimeEnabled;
      const overtimeMultiplier = Number(
        config.overtimeMultiplier ?? cfgOvertimeMultiplier ?? 1.5,
      );

      // 🔁 Effective overtime detection rules
      const ttCfg = ttOvertimeConfig || {};
      // Basis can be:
      // - "period"  → total hours in pay period (40, 80, etc)
      // - "daily"   → 8+ per day
      // - legacy "weekly" which we normalize to "period"
      const rawBasis =
        ttCfg.overtimeBasis || config.overtimeBasis /* legacy */ || "period";

      const overtimeBasis =
        rawBasis === "weekly" ? "period" : rawBasis || "period";

      const periodThreshold =
        typeof ttCfg.hoursBeforeOvertimePerPeriod === "number"
          ? ttCfg.hoursBeforeOvertimePerPeriod
          : typeof config.overtimeWeeklyHours === "number"
          ? config.overtimeWeeklyHours
          : typeof config.overtimeHours === "number"
          ? config.overtimeHours
          : 40;

      const dailyThreshold =
        typeof ttCfg.dailyOvertimeHours === "number"
          ? ttCfg.dailyOvertimeHours
          : typeof config.overtimeDailyHours === "number"
          ? config.overtimeDailyHours
          : 8;

      // Build rows with employee info + gross pay
      const rowList = Object.entries(agg).map(([userId, stats]) => {
        const emp = employees[userId] || {};
        const name =
          emp.name || emp.fullName || emp.displayName || "Unknown employee";
        const jobTitle = emp.jobTitle || emp.position || "—";
        const department =
          emp.department || emp.group || emp.departmentName || "—";

        // Determine worksite label for this employee row
        const siteIds = Object.keys(stats.siteHours || {});
        let workSiteName = "—";
        let workSiteId = null;

        if (selectedWorkSiteId !== "all") {
          workSiteId = selectedWorkSiteId;
          workSiteName =
            stats.siteNames?.[selectedWorkSiteId] ||
            workSiteOptions.find((s) => s.id === selectedWorkSiteId)?.name ||
            "Selected site";
        } else if (siteIds.length === 1) {
          workSiteId = siteIds[0];
          workSiteName = stats.siteNames?.[workSiteId] || "Unnamed site";
        } else if (siteIds.length > 1) {
          // Pick primary by most hours
          let bestId = siteIds[0];
          siteIds.forEach((sid) => {
            if ((stats.siteHours[sid] || 0) > (stats.siteHours[bestId] || 0))
              bestId = sid;
          });
          workSiteId = bestId;
          const primaryName = stats.siteNames?.[bestId] || "Unnamed site";
          workSiteName = `Multiple (primary: ${primaryName})`;
        }

        // Try a few possible field names for hourly rate
        const rateRaw =
          emp.hourlyRate ?? emp.hourly_rate ?? emp.payRate ?? emp.pay_rate ?? 0;
        const rate = Number(rateRaw || 0);

        const workedHours = Number((stats.workHours || 0).toFixed(2));
        const paidLeaveHours = Number((stats.paidLeaveHours || 0).toFixed(2));
        const unpaidLeaveHours = Number(
          (stats.unpaidLeaveHours || 0).toFixed(2),
        );

        // Total payable hours = worked + paid leave
        const totalHours = Number((workedHours + paidLeaveHours).toFixed(2));

        let regularHours = workedHours;
        let overtimeHours = 0;

        // 🔹 Overtime hours are *detected* using Time Tracking rules,
        //     but only *paid* extra if overtimePayOn is true.
        if (rate > 0 && workedHours > 0) {
          if (overtimeBasis === "period") {
            // Total hours in this pay period (already scoped by effectiveRange)
            const threshold = periodThreshold > 0 ? periodThreshold : 0;
            const totalWorkInPeriod = workedHours;

            const reg = Math.min(totalWorkInPeriod, threshold);
            const ot = Math.max(0, totalWorkInPeriod - threshold);

            regularHours = Number(reg.toFixed(2));
            overtimeHours = Number(ot.toFixed(2));
          } else if (overtimeBasis === "daily") {
            const threshold = dailyThreshold > 0 ? dailyThreshold : 0;

            // Daily overtime: per-day threshold (work hours only)
            let reg = 0;
            let ot = 0;
            Object.values(stats.perDayWork || {}).forEach((h) => {
              const hh = Number(h || 0);
              const dayReg = Math.min(hh, threshold);
              const dayOt = Math.max(0, hh - threshold);
              reg += dayReg;
              ot += dayOt;
            });
            regularHours = Number(reg.toFixed(2));
            overtimeHours = Number(ot.toFixed(2));
          }
        }

        // If payroll overtime pay is OFF, treat OT hours as regular in pay computation
        let workRegularForPay = regularHours;
        let workOvertimeForPay = overtimeHours;
        if (!overtimePayOn) {
          workRegularForPay = Number((regularHours + overtimeHours).toFixed(2));
          workOvertimeForPay = 0;
        }

        // 🔹 Pay: work (regular + OT) + paid leave (at base rate)
        const workPay =
          workRegularForPay * rate +
          workOvertimeForPay * rate * overtimeMultiplier;
        const paidLeavePay = paidLeaveHours * rate;
        const grossPay = workPay + paidLeavePay;

        return {
          userId,
          name,
          jobTitle,
          department,
          workSiteId, // ✅ NEW
          workSiteName, // ✅ NEW
          // breakdown
          workedHours,
          paidLeaveHours,
          unpaidLeaveHours,
          hours: totalHours, // total payable hours used in summary
          regularHours,
          overtimeHours,
          hourlyRate: rate,
          grossPay: Number(grossPay.toFixed(2)),
          entries: stats.entries,
        };
      });

      // Sort alphabetically by name
      rowList.sort((a, b) => a.name.localeCompare(b.name));

      setRows(rowList);
    } catch (e) {
      console.error("Error generating payroll report", e);
      setError("Failed to generate payroll report.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExportCsv = () => {
    if (!rows.length || !effectiveRange) return;

    const { start, end } = effectiveRange;
    const startISO = start.toISODate();
    const endISO = end.toISODate();

    const header = [
      "Employee",
      "Job Title",
      "Department",
      "Worksite", // ✅ NEW
      "Entries",
      "Worked Hours",
      "Paid Leave Hours",
      "Unpaid Leave Hours",
      "Total Payable Hours",
      "Regular Hours",
      "OT Hours",
      "Hourly Rate",
      "Gross Pay",
      "Period Start",
      "Period End",
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      // Wrap in quotes if it has , " or newline
      if (/[",\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const lines = [
      header.map(escapeCsv).join(","),
      ...rows.map((r) =>
        [
          r.name,
          r.jobTitle,
          r.department,
          r.workSiteName || "—", // ✅ NEW
          r.entries,
          r.workedHours.toFixed(2),
          r.paidLeaveHours.toFixed(2),
          r.unpaidLeaveHours.toFixed(2),
          r.hours.toFixed(2),
          r.regularHours.toFixed(2),
          r.overtimeHours.toFixed(2),
          r.hourlyRate.toFixed(2),
          r.grossPay.toFixed(2),
          startISO,
          endISO,
        ]
          .map(escapeCsv)
          .join(","),
      ),
    ];

    const csv = lines.join("\n");
    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `payroll_${startISO}_to_${endISO}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Totals
  const totals = useMemo(() => {
    if (!rows.length)
      return { employees: 0, hours: 0, regular: 0, overtime: 0, gross: 0 };
    const employeesCount = rows.length;
    const hours = rows.reduce((sum, r) => sum + (r.hours || 0), 0); // total payable
    const regular = rows.reduce((sum, r) => sum + (r.regularHours || 0), 0);
    const overtime = rows.reduce((sum, r) => sum + (r.overtimeHours || 0), 0);
    const gross = rows.reduce((sum, r) => sum + (r.grossPay || 0), 0);
    return {
      employees: employeesCount,
      hours: Number(hours.toFixed(2)),
      regular: Number(regular.toFixed(2)),
      overtime: Number(overtime.toFixed(2)),
      gross: Number(gross.toFixed(2)),
    };
  }, [rows]);

  // ── Payroll settings modal handlers ──────────────────────────────
  const openConfigModal = () => {
    // Make sure modal reflects latest config when opened
    if (payrollConfig) {
      setCfgFrequency(payrollConfig.frequency || "weekly");
      setCfgAnchorDate(payrollConfig.anchorDate || "");

      setCfgOvertimeEnabled(!!payrollConfig.overtimeEnabled);
      setCfgOvertimeMultiplier(payrollConfig.overtimeMultiplier || 1.5);
    }
    setShowConfigModal(true);
  };

  const handleSaveConfig = async () => {
    if (!companyId) return;
    setSavingConfig(true);
    setError("");

    try {
      const cfg = {
        frequency: cfgFrequency, // weekly | biweekly | semimonthly
        anchorDate: cfgAnchorDate || null,
        overtimeEnabled: cfgOvertimeEnabled,
        // For backwards compatibility we keep these fields, but
        // overtime detection actually comes from Time Tracking settings.
        overtimeBasis: "period",
        overtimeWeeklyHours: null,
        overtimeDailyHours: null,
        overtimeMultiplier: Number(cfgOvertimeMultiplier) || 1,
        updatedAt: Date.now(),
      };

      const companyRef = doc(db, "companies", companyId);
      await updateDoc(companyRef, {
        payrollConfig: cfg,
      });

      // Mark first-run configured
      try {
        await setPayrollConfigured(db, companyId);
      } catch (e) {
        console.warn("Failed to set payrollConfigured flag", e);
      }

      setPayrollConfig(cfg);
      setShowConfigModal(false);
      setPayrollBannerOpen(false);

      // Sync main controls with saved settings
      if (["weekly", "biweekly", "semimonthly"].includes(cfg.frequency)) {
        setPeriodType(cfg.frequency);
      }
      if (cfg.anchorDate) {
        setStartDate(cfg.anchorDate);
      }

      toast.success("Payroll settings saved.");
    } catch (e) {
      console.error("Failed to save payroll settings", e);
      setError("Could not save payroll settings.");
      toast.error("Failed to save payroll settings.");
    } finally {
      setSavingConfig(false);
    }
  };

  // ── RENDER ───────────────────────────────────────────────────────
  if (loadingInit) {
    return (
      <div className="page-title-container">
        <div className="payroll-wrapper">
          <PageLoader label="Loading payroll…" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-title-container">
      <div className="payroll-wrapper">
        <ToastContainer />

        {/* First-run banner (same vibe as Expense) */}
        {payrollBannerOpen && (
          <div
            style={{
              marginTop: 12,
              marginBottom: 12,
              padding: "10px 12px",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              background: "#f8fafc",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
            role="status"
            aria-live="polite"
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span aria-hidden>📊</span>
              <div>
                <strong>Complete Payroll setup</strong>
                <div style={{ color: "#64748b", fontSize: 13 }}>
                  Configure your pay period and overtime pay rules. Overtime
                  hours are detected from Time Tracking settings.
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="expense-settings-btn"
                onClick={openConfigModal}
                title="Payroll Settings"
              >
                Open Settings
              </button>
              <button
                type="button"
                className="timesheetbtn ghost"
                onClick={() => setPayrollBannerOpen(false)}
                title="Hide reminder for now"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        <header className="payroll-header">
          <div>
            <h1>Payroll</h1>
            <p className="payroll-subtitle">
              Generate pay period summaries using time tracking, leave, and
              employee pay rates.
            </p>
          </div>

          <button
            type="button"
            className="payroll-settings-btn"
            onClick={openConfigModal}
          >
            <SlidersHorizontal className="payroll-settings-icon" size={16} />
            <span>Payroll settings</span>
          </button>
        </header>

        {/* ── Settings row ───────────────────────────── */}
        <section className="payroll-controls">
          {/* Employee filter */}
          <div className="payroll-field-group">
            <label className="payroll-label">Employee</label>
            <select
              className="payroll-input"
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
            >
              <option value="all">All employees</option>
              {employeeOptions.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name ||
                    emp.fullName ||
                    emp.displayName ||
                    "Unnamed employee"}
                </option>
              ))}
            </select>
          </div>

          {/* ✅ NEW: Worksite filter */}
          <div className="payroll-field-group">
            <label className="payroll-label">Worksite</label>
            <select
              className="payroll-input"
              value={selectedWorkSiteId}
              onChange={(e) => setSelectedWorkSiteId(e.target.value)}
            >
              <option value="all">All worksites</option>
              {workSiteOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Read-only pay schedule info (from settings) */}
          <div className="payroll-field-group">
            <label className="payroll-label">Pay schedule</label>
            <div className="payroll-schedule-meta">
              <span className="payroll-schedule-pill">{scheduleLabel}</span>
              <span className="payroll-schedule-anchor">
                Anchor date: {anchorDisplay}
              </span>
            </div>
          </div>

          {/* Cycle start date */}
          <div className="payroll-field-group">
            <label className="payroll-label">Pay period start</label>
            <input
              type="date"
              className="payroll-input"
              value={startDate}
              onChange={(e) => {
                setPeriodMode("manual");
                setStartDate(e.target.value);
              }}
            />
          </div>

          {/* End date (auto) */}
          <div className="payroll-field-group">
            <label className="payroll-label">Pay period end (auto)</label>
            <input
              type="date"
              className="payroll-input"
              value={effectiveRange?.end?.toISODate() || ""}
              disabled
            />
            <div className="payroll-field-group">
              <label className="payroll-label">Pay period</label>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  className="payroll-export-btn"
                  onClick={goPrev}
                >
                  ◀ Prev
                </button>

                <button
                  type="button"
                  className="payroll-generate-btn"
                  onClick={goToMostRecentEnded}
                  title="Auto-pick the most recently ended pay period"
                >
                  Current
                </button>

                <button
                  type="button"
                  className="payroll-export-btn"
                  onClick={goNext}
                >
                  Next ▶
                </button>

                <span style={{ fontSize: 12, color: "#64748b" }}>
                  Mode:{" "}
                  <strong>{periodMode === "auto" ? "Auto" : "Manual"}</strong>
                </span>
              </div>
            </div>
          </div>

          <div className="payroll-actions">
            <div className="payroll-actions-container">
              <button
                type="button"
                className="payroll-generate-btn"
                onClick={handleGenerate}
                disabled={!startDate || isGenerating}
              >
                {isGenerating ? "Generating…" : "Generate report"}
              </button>
            </div>
            <div className="payroll-export-container">
              <button
                type="button"
                className="payroll-export-btn"
                onClick={handleExportCsv}
                disabled={!rows.length}
              >
                Export CSV
              </button>
            </div>
          </div>
        </section>

        {/* ── Body: summary + table ───────────────────── */}
        <div className="payroll-body">
          {isGenerating ? (
            <PageLoader label="Generating payroll report…" />
          ) : (
            <>
              {error && <p className="payroll-error">{error}</p>}

              {/* Summary strip */}
              <div className="payroll-summary">
                <div>
                  <span className="summary-label">Employees</span>
                  <div className="summary-value">{totals.employees}</div>
                </div>
                <div>
                  <span className="summary-label">Total hours</span>
                  <div className="summary-value">{totals.hours.toFixed(2)}</div>
                </div>
                <div>
                  <span className="summary-label">Regular / OT hours</span>
                  <div className="summary-value">
                    {totals.regular.toFixed(2)} reg ·{" "}
                    {totals.overtime.toFixed(2)} OT
                  </div>
                </div>
                <div>
                  <span className="summary-label">Estimated gross pay</span>
                  <div className="summary-value">{money(totals.gross)}</div>

                  {/* Pay period label */}
                  {rangeLabel && (
                    <div className="payroll-range-label">
                      Pay period: <strong>{rangeLabel}</strong>
                    </div>
                  )}
                </div>
              </div>

              {/* Table or empty state */}
              {!rows.length ? (
                <div className="payroll-empty-state">
                  <h2>Payroll report setup</h2>
                  <p>
                    Choose a pay period and click{" "}
                    <strong>Generate report</strong> to see hours and estimated
                    gross pay for each employee.
                  </p>
                  <p className="payroll-note">
                    This report uses <strong>hours from time tracking</strong>{" "}
                    and each employee&apos;s <strong>hourly rate</strong>.
                    Overtime hours are detected based on your{" "}
                    <strong>Time Tracking overtime rules</strong>, and Payroll
                    can optionally pay them at a higher rate.
                  </p>
                </div>
              ) : (
                <div className="payroll-table-card">
                  <div className="payroll-table-scroll">
                    <table className="payroll-table">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Job Title</th>
                          <th>Department</th>
                          <th>Worksite</th> {/* ✅ NEW */}
                          <th>Entries</th>
                          <th>Worked hours</th>
                          <th>Paid leave hours</th>
                          <th>Unpaid leave hours</th>
                          <th>Total payable hours</th>
                          <th>Regular hours</th>
                          <th>OT hours</th>
                          <th>Hourly rate</th>
                          <th>Gross pay</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => (
                          <tr key={row.userId}>
                            <td>{row.name}</td>
                            <td>{row.jobTitle}</td>
                            <td>{row.department}</td>
                            <td>{row.workSiteName || "—"}</td> {/* ✅ NEW */}
                            <td>{row.entries}</td>
                            <td>{row.workedHours.toFixed(2)}</td>
                            <td>{row.paidLeaveHours.toFixed(2)}</td>
                            <td>{row.unpaidLeaveHours.toFixed(2)}</td>
                            <td>{row.hours.toFixed(2)}</td>
                            <td>{row.regularHours.toFixed(2)}</td>
                            <td>{row.overtimeHours.toFixed(2)}</td>
                            <td>{money(row.hourlyRate)}</td>
                            <td>{money(row.grossPay)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Payroll Settings Modal ───────────────────── */}
      {showConfigModal && (
        <div className="payroll-modal-backdrop">
          <div className="payroll-modal">
            {/* Header */}
            <div className="payroll-modal-header-row">
              <div>
                <h3 className="payroll-modal-title">Payroll settings</h3>
                <p className="payroll-modal-subtitle">
                  Set up how often you pay employees and whether overtime hours
                  should be paid at a higher rate. Overtime hours are detected
                  using your Time Tracking overtime rules.
                </p>
              </div>
              <div className="payroll-modal-badge">
                <span> Takes about 1 minute</span>
              </div>
            </div>

            {/* 3-step guide */}
            <div className="payroll-steps-row">
              <div className="payroll-step-card">
                <span className="payroll-step-number">1</span>
                <h5>Choose pay schedule</h5>
                <p>
                  How often employees are paid (weekly, bi-weekly, or
                  semi-monthly).
                </p>
              </div>
              <div className="payroll-step-card">
                <span className="payroll-step-number">2</span>
                <h5>Set first pay period date</h5>
                <p>Tell us the first day your pay cycles should start from.</p>
              </div>
              <div className="payroll-step-card">
                <span className="payroll-step-number">3</span>
                <h5>Configure overtime pay (optional)</h5>
                <p>
                  Decide if overtime hours (detected by Time Tracking rules)
                  should be paid at a higher rate.
                </p>
              </div>
            </div>

            {/* Pay schedule */}
            <div className="payroll-modal-section">
              <div className="payroll-section-header">
                <h4>Step 1 – Pay schedule</h4>
                <p className="payroll-section-caption">
                  This controls how the Payroll report groups hours into pay
                  periods.
                </p>
              </div>

              <div className="payroll-radio-group">
                <label className="payroll-radio">
                  <input
                    type="radio"
                    name="payFrequency"
                    value="weekly"
                    checked={cfgFrequency === "weekly"}
                    onChange={() => setCfgFrequency("weekly")}
                  />
                  <span>
                    Weekly
                    <small>
                      Employees are paid every week (52 pay runs per year).
                    </small>
                  </span>
                </label>
                <label className="payroll-radio">
                  <input
                    type="radio"
                    name="payFrequency"
                    value="biweekly"
                    checked={cfgFrequency === "biweekly"}
                    onChange={() => setCfgFrequency("biweekly")}
                  />
                  <span>
                    Bi-weekly
                    <small>
                      Employees are paid every 2 weeks (26 pay runs per year).
                    </small>
                  </span>
                </label>
                <label className="payroll-radio">
                  <input
                    type="radio"
                    name="payFrequency"
                    value="semimonthly"
                    checked={cfgFrequency === "semimonthly"}
                    onChange={() => setCfgFrequency("semimonthly")}
                  />
                  <span>
                    Semi-monthly
                    <small>
                      Employees are paid twice per month (e.g. 1st &amp; 15th).
                    </small>
                  </span>
                </label>
              </div>

              <label className="payroll-label" style={{ marginTop: 10 }}>
                Step 2 – Payroll cycle anchor date
              </label>
              <input
                type="date"
                className="payroll-input"
                value={cfgAnchorDate || ""}
                onChange={(e) => setCfgAnchorDate(e.target.value)}
              />
              <p className="payroll-hint">
                Pick the first day your pay cycles are based on. For example, if
                you pay weekly on Fridays, choose the Monday of that first pay
                week.
              </p>
            </div>

            {/* Overtime pay rules */}
            <div className="payroll-modal-section">
              <div className="payroll-section-header">
                <h4>Step 3 – Overtime pay (optional)</h4>
                <p className="payroll-section-caption">
                  Turn this on if you pay a higher rate for overtime hours. When
                  overtime pay is off, overtime hours are still detected and
                  shown in reports, but paid at the regular rate.
                </p>
              </div>

              <label className="payroll-checkbox">
                <input
                  type="checkbox"
                  checked={cfgOvertimeEnabled}
                  onChange={(e) => setCfgOvertimeEnabled(e.target.checked)}
                />
                <span>Enable overtime pay in Payroll</span>
              </label>

              {!cfgOvertimeEnabled && (
                <p className="payroll-hint">
                  Overtime hours are still detected based on your{" "}
                  <strong>Time Tracking overtime rules</strong>, but in Payroll
                  they&apos;ll be paid at the regular rate.
                </p>
              )}

              {cfgOvertimeEnabled && (
                <>
                  <p className="payroll-hint">
                    Overtime hours are determined by your{" "}
                    <strong>Time Tracking &gt; Time Tracking Settings</strong>{" "}
                    page (overtime detection). Here, you only control how much
                    extra to pay for those overtime hours.
                  </p>
                  <div className="payroll-overtime-inputs">
                    <div>
                      <label className="payroll-label">
                        Overtime multiplier
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        className="payroll-input"
                        value={cfgOvertimeMultiplier}
                        onChange={(e) =>
                          setCfgOvertimeMultiplier(Number(e.target.value) || 0)
                        }
                      />
                      <p className="payroll-hint">
                        Example: <strong>1.5</strong> = “time &amp; a half”,
                        <strong> 2.0</strong> = “double time”.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Live summary so employers can double-check */}
            <div className="payroll-config-summary">
              <h4>Quick summary</h4>
              <p>
                Pay schedule: <strong>{scheduleLabel}</strong>
                {" · "}
                Anchor date: <strong>{anchorDisplay}</strong>
              </p>
              <p>
                {cfgOvertimeEnabled ? (
                  <>
                    Overtime pay is <strong>ON</strong>. Any overtime hours
                    (based on your <strong>Time Tracking overtime rules</strong>
                    ) will be paid at{" "}
                    <strong>{cfgOvertimeMultiplier || 1}×</strong> the regular
                    rate in Payroll.
                  </>
                ) : (
                  <>
                    Overtime pay is <strong>OFF</strong>. Overtime hours may
                    still appear in reports, but Payroll treats all payable
                    hours at the regular rate.
                  </>
                )}
              </p>
            </div>

            {/* Footer */}
            <div className="payroll-modal-footer">
              <button
                type="button"
                className="payroll-modal-secondary"
                onClick={() => setShowConfigModal(false)}
                disabled={savingConfig}
              >
                Cancel
              </button>
              <button
                type="button"
                className="payroll-modal-primary"
                onClick={handleSaveConfig}
                disabled={savingConfig}
              >
                {savingConfig ? "Saving…" : "Save settings"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
